/**
 * MobileBottomToolbar — S5 mobile bottom floating toolbar.
 *
 * Fixed, semi-transparent, icon-only nav bar with five destinations:
 *
 *  🔥  Fire    → 'home'         (Hot Leads)
 *  ▽   Funnel  → 'pipeline'     (My Pipeline)
 *  ✉️  Bot     → 'autopilot'    (Autopilot — real page as of Jul 30 2026.
 *                                 Salesperson: own-claimed-leads messaging.
 *                                 Manager/admin/super_admin: global dealer-
 *                                 wide messaging. See AutopilotView.jsx.)
 *  🔍  Search  → 'search'       (TODO: search page not yet implemented — icon is greyed /
 *                                 disabled; wire up once the Search route exists)
 *  ⚙️  Gear    → 'admin'        (Admin / Settings)
 *
 * Props:
 *   currentView  string          — active view id from App.jsx state
 *   onNav        (viewId) => void — navigate to a view
 *
 * Icon library: lucide-react (already used throughout the codebase).
 */

import React from 'react';
import { Flame, Filter, BotMessageSquare, Search, Settings } from 'lucide-react';

// TODO: Wire 'search' to a real Search page/route once it is built.
//       Until then the icon renders greyed and the button is disabled.
const SEARCH_ENABLED = false;

export default function MobileBottomToolbar({ currentView, onNav }) {
  const items = [
    {
      id: 'home',
      label: 'Hot Leads',
      Icon: Flame,
    },
    {
      id: 'pipeline',
      label: 'Pipeline',
      Icon: Filter,
    },
    {
      id: 'autopilot',
      label: 'Autopilot',
      Icon: BotMessageSquare,
    },
    {
      id: 'search',
      label: 'Search',
      Icon: Search,
      // TODO: enable once a Search page/route exists
      disabled: !SEARCH_ENABLED,
    },
    {
      id: 'admin',
      label: 'Settings',
      Icon: Settings,
    },
  ];

  return (
    <nav style={styles.bar} aria-label="Main navigation">
      {items.map((item, idx) => {
        const isActive = currentView === item.id;
        const isDisabled = !!item.disabled;

        return (
          <button
            key={idx}
            style={{
              ...styles.btn,
              ...(isActive ? styles.btnActive : {}),
              ...(isDisabled ? styles.btnDisabled : {}),
            }}
            onClick={() => !isDisabled && onNav(item.id)}
            disabled={isDisabled}
            aria-label={item.label}
            title={isDisabled ? `${item.label} (coming soon)` : item.label}
            aria-current={isActive ? 'page' : undefined}
          >
            <item.Icon
              size={22}
              strokeWidth={isActive ? 2.4 : 1.8}
              color={
                isDisabled
                  ? '#475569'
                  : isActive
                  ? 'var(--brand-primary, #317eeb)'
                  : '#94a3b8'
              }
            />
            <span
              style={{
                ...styles.label,
                color: isDisabled
                  ? '#475569'
                  : isActive
                  ? 'var(--brand-primary, #317eeb)'
                  : '#94a3b8',
              }}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

const styles = {
  bar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    height: '60px',
    padding: '0 4px',
    // Semi-transparent dark glass
    background: 'rgba(11, 18, 32, 0.88)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderTop: '1px solid rgba(44, 53, 78, 0.55)',
    // Subtle bottom safe-area padding for notch devices
    paddingBottom: 'env(safe-area-inset-bottom, 0)',
  },
  btn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px',
    flex: 1,
    height: '100%',
    padding: '6px 0',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'background 0.15s',
  },
  btnActive: {
    background: 'rgba(49, 126, 235, 0.10)',
  },
  btnDisabled: {
    cursor: 'not-allowed',
    opacity: 0.45,
  },
  label: {
    fontSize: '9px',
    fontFamily: "'Noto Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontWeight: 500,
    letterSpacing: '0.02em',
    lineHeight: 1,
  },
};
